import React, { useEffect, useState } from 'react';

import LabelValue from '@/components/core/LabelValue/LabelValue';
import { useDataModel } from '@/hooks/useDataModel';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import useEnumAttributeService from '@/hooks/useEnumAttributeService';
import { useTranslation } from '@/hooks/useTranslation';
import { getFilter } from '@/services/angularjs-bridge';
import { DataModelAttribute, DataModelAttributeBaseClass, DataModelAttributeOption, DataModelAttributeTypeName, Item } from '@/types/DataModel';

import { getMemberAttributes } from '../datamodel/Collection/useMemberAttributes';
import { useAttributeLabelTranslator } from '../datamodel/hooks/useAttributeLabelTranslator';
import { useCachedAdditionalCategory } from '../datamodel/hooks/useCachedAdditionalCategory';
import { useOptionTranslator } from '../datamodel/hooks/useOptionTranslator';

interface LabelValueAttributeProps {
    attribute: DataModelAttribute;
    item: Item;
    icon?: React.ReactNode;
    className?: string;
}

const LabelValueAttribute: React.FC<LabelValueAttributeProps> = ({
    attribute,
    item,
    icon,
    className,
}) => {
    const attributeData = item[attribute.name];
    const additionalDataModelName = attribute.params?.additionalModule;
    const extension = attribute.params?.extension;

    const { translateOption } = useOptionTranslator();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const { formatDate } = useDateFormatter();
    const { dataModel } = useDataModel();
    const { enumAttributeService } = useEnumAttributeService();
    const { t } = useTranslation();

    const [options, setOptions] = useState<DataModelAttributeOption[]>([]);
    const [loadingOptions, setLoadingOptions] = useState(true);

    const { cachedAdditionalCategory, isLoading: isLoadingSelectedOption } =
        useCachedAdditionalCategory(attributeData, additionalDataModelName, extension);

    useEffect(() => {
        if (
            attribute.typeName === DataModelAttributeTypeName.Enum ||
            attribute.typeName === DataModelAttributeTypeName.Codelist
        ) {
            enumAttributeService?.getAttributeOptionsAsync(attribute, true).then(
                (fetchedOptions: DataModelAttributeOption[]) => {
                    setOptions(fetchedOptions);
                    setLoadingOptions(false);
                }
            );
        }
    }, [attribute, enumAttributeService]);

    const label = translateAttributeLabel(attribute);

    const renderWith = (value: React.ReactNode, keySuffix?: string) => (
        <LabelValue
            label={label}
            value={value}
            keySuffix={keySuffix}
            icon={icon}
            className={className}
        />
    );

    const renderEmpty = () => renderWith('—');

    if (attributeData === undefined || attributeData === null || attributeData === '') {
        return renderEmpty();
    }

    switch (attribute.typeName || attribute.baseClass) {
        case DataModelAttributeTypeName.Document:
            return renderWith(
                <a href={String(attributeData)} target="_blank" rel="noreferrer">
                    {String(attributeData)}
                </a>,
            );

        case DataModelAttributeTypeName.Image:
            return renderWith(String(attributeData));

        case DataModelAttributeTypeName.Enum:
        case DataModelAttributeTypeName.Codelist:
        case DataModelAttributeTypeName.OpenEnum:
        case DataModelAttributeTypeName.OpenCodelist:
        case DataModelAttributeTypeName.Boolean: {
            if (loadingOptions) {
                return renderWith(translateOption(String(attributeData), attribute));
            }
            return renderWith(translateOption(String(attributeData), attribute, options));
        }

        case DataModelAttributeTypeName.EnumSet:
        case DataModelAttributeTypeName.CodelistSet:
        case DataModelAttributeTypeName.OpenEnumSet:
        case DataModelAttributeTypeName.OpenCodelistSet: {
            const entries = Array.isArray(attributeData)
                ? attributeData
                : Object.values(attributeData ?? {});
            const translated = entries
                .map((entry) => translateOption(String(entry), attribute, attribute.options))
                .join(', ');
            return renderWith(translated);
        }

        case DataModelAttributeTypeName.Date:
            return renderWith(formatDate(attributeData as string, 'mediumDate'));

        case DataModelAttributeTypeName.DateTime:
            return renderWith(formatDate(attributeData as string, 'medium'));

        case DataModelAttributeTypeName.Physical: {
            const filter = getFilter('formatPhysicalValue');
            const formatted = filter ? filter(attributeData) : String(attributeData);
            const parts = typeof formatted === 'string' ? formatted.trim().split(/\s+/) : [];
            if (parts.length >= 2) {
                const unit = parts[parts.length - 1];
                const number = parts.slice(0, -1).join(' ');
                return renderWith(number, unit);
            }
            return renderWith(formatted);
        }

        case DataModelAttributeTypeName.AdditionalCategory:
            if (isLoadingSelectedOption || !cachedAdditionalCategory) {
                return renderWith(String(attributeData));
            }
            return renderWith(
                `${cachedAdditionalCategory.title} [${cachedAdditionalCategory.code}]`,
            );

        case DataModelAttributeTypeName.Collection:
        case DataModelAttributeTypeName.MultiDimensional:
        case DataModelAttributeTypeName.MultiReference: {
            const count = Array.isArray(attributeData) ? attributeData.length : 0;
            const translationKey = 'attribute.collection.count';
            const translated = t(translationKey, { count });
            const summary = translated && translated !== translationKey
                ? translated
                : `${count}`;
            return renderWith(summary);
        }

        case DataModelAttributeTypeName.SingleReference:
            return renderWith(String(attributeData).replace('::', '-'));

        case DataModelAttributeTypeName.Dimensional: {
            if (typeof attributeData !== 'object' || attributeData === null) {
                return renderWith(String(attributeData));
            }
            const entries = Object.entries(attributeData as Record<string, unknown>);
            if (entries.length === 0) {
                return renderEmpty();
            }
            const [firstKey, firstValue] = entries[0];
            const translatedKey = translateOption(firstKey, attribute, attribute.options);
            return renderWith(String(firstValue), translatedKey);
        }

        case DataModelAttributeTypeName.Group: {
            const valueAttributeName = attribute.params?.valueAttribute as string | undefined;
            const memberAttributes = getMemberAttributes(attribute, dataModel);
            const firstEntry = Array.isArray(attributeData) ? attributeData[0] : attributeData;
            const valueAttribute =
                memberAttributes?.find((member) => member.name === valueAttributeName)
                ?? memberAttributes?.[0];

            if (!firstEntry || !valueAttribute) {
                const count = Array.isArray(attributeData) ? attributeData.length : 1;
                return renderWith(String(count));
            }

            const innerValue = firstEntry?.[valueAttribute.name];
            return renderWith(innerValue !== undefined ? String(innerValue) : '');
        }

        case DataModelAttributeBaseClass.Hash:
            return renderWith(JSON.stringify(attributeData));

        default:
            return renderWith(String(attributeData));
    }
};

export default LabelValueAttribute;
